import OrganizationApi from '@/api/OrganizationApi';
import { i18n } from '@/i18n';
import { sanitizeMailPrefix } from '@/utils/tools';
import { useModal, useNotif } from '@orion.ui/orion';
import { defineAsyncComponent } from 'vue';

class OrganizationService {

	generateMail (payload: string) {
		const mail = sanitizeMailPrefix(payload);
		return `${mail}@myarmado.fr`;
	}

	async checkOrganizationNameAndEmailAsync (name: string, email: string) {
		try {
			await OrganizationApi.checkOrganizationNameAndEmail({
				name,
				email,
			});
			return {
				success: true,
				message: undefined,
			};
		}
		catch (e: any) {
			return {
				success: false,
				message: e.response.data,
			};
		}
	}

	async getDetailsByIdAsync (id: number) {
		const { data } = await OrganizationApi.selectById(id);
		return data;
	}

	async updateNameAndEmailAsync (id: number, payload: IUpdateOrganizationModel) {
		await OrganizationApi.update(id, payload);
		useNotif.success(i18n.global.t('common.label.saving'), i18n.global.t('business.organizationNameAndEmailModified'));
	}

	async getDashboardStatsAsync () {
		const { data } = await OrganizationApi.dashboardStatistic();
		return data;
	}

	async showReleaseNoteModalAsync () {
		return new Promise<void>(async (resolve) => {
			useModal({
				Nested: defineAsyncComponent(() => import('@/components/organization/OrganizationReleaseNote.vue')),
				size: 'sm',
				events: { 'leave-end': () => resolve() },
			});
		});
	}

	async createOrganizationFusionRequestAsync () {
		return new Promise<void>(async (resolve) => {
			useModal({
				Nested: defineAsyncComponent(() => import('@/components/organization/OrganizationFusionRequestModal.vue')),
				size: 'sm',
				events: { 'leave-end': () => resolve() },
			});
		});
	}

	async openOrganizationModalNameUpdateAsync (organization: { name: string, email: string }) {
		return new Promise<{ name: string, email: string }>(async (resolve) => {
			useModal({
				size: 'sm',
				Nested: defineAsyncComponent(() => import('@/components/organization/OrganizationModalNameUpdate.vue')),
				NestedProps: { organization },
				events: {
					saved: (Modal, payload: { name: string, email: string }) => {
						resolve(payload);
						Modal.close();
					},
				},
			});

		});
	}

	async openOrganizationModalCreatorTransferAsync () {
		return new Promise(async (resolve) => {
			useModal({
				Nested: defineAsyncComponent(() => import('@/components/organization/OrganizationModalCreatorTransfer.vue')),
				NestedProps: { resolve },
				size: 'sm',
			});
		});
	}

	async listAsync (...args: Parameters<typeof OrganizationApi.list>) {
		const { data } = await OrganizationApi.list(...args);
		return data;
	}

	async getOverviewMarkersAsync (...args: Parameters<typeof OrganizationApi.overviewMarkers>) {
		const { data } = await OrganizationApi.overviewMarkers(...args);
		return data;
	}

	async addCompanyAsync (...args: Parameters<typeof OrganizationApi.addCompany>) {
		const { data } = await OrganizationApi.addCompany(...args);
		return data;
	}

	async searchAsync (...args: Parameters<typeof OrganizationApi.search>) {
		const { data } = await OrganizationApi.search(...args);
		return data;
	}

	async gridAvailableCompaniesAsync (...args: Parameters<typeof OrganizationApi.gridAvailableCompanies>) {
		const { data } = await OrganizationApi.gridAvailableCompanies(...args);
		return data;
	}

	async addAsync (...args: Parameters<typeof OrganizationApi.add>) {
		const { data } = await OrganizationApi.add(...args);
		return data;
	}

}

export default function useOrganizationService () {
	return new OrganizationService();
}
